import { useAutoAnimate } from "@formkit/auto-animate/react";
import { BoxIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import { getAddOns, getSortedAddOns } from "common";
import React from "react";
import { useHistory } from "react-router-dom";
import { AddOn, CatalogItem, CatalogItemObject, CatalogType, Experience, LineItem, LineItemType } from "types";
import { AddonLineItem, CatalogLineItem, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { ExpandableList } from "../Common/ExpandableList";
import { BooleanAddon } from "../PurchaseModification/Addons/BooleanAddons";
import { ChoicesAddon } from "../PurchaseModification/Addons/ChoicesAddons";
import { QuantityAddon } from "../PurchaseModification/Addons/QuantityAddons";
import { useAddOnLimits } from "./AddOnLimitsContext";
import { useCatalogItems } from "@/hooks/resources/useCatalogItems";
import { isQuestionnaireAddOn } from "@/models/CatalogItem";

export interface CreatePurchaseAddOnListProps {
    /** Also shows the purchase's add-ons that the current experience no longer offers. */
    shouldShowPurchaseAddons?: boolean;
    experience?: Experience;
    lineItems?: any;
    catalogItems?: CatalogItem[];
    purchaseId?: ID;
    onLineItemUpdate: (quantity: number, templateId?: ID) => void;
    onChoiceLineItemUpdate: (templateIds: ID[], selectedItemId: ID, shouldDecreaseOnly: boolean) => void;
}

export const CreatePurchaseAddOnList = React.memo(
    ({
        shouldShowPurchaseAddons = false,
        lineItems: itemLineItems,
        experience: itemExperience,
        catalogItems,
        purchaseId,
        onLineItemUpdate,
        onChoiceLineItemUpdate,
    }: CreatePurchaseAddOnListProps) => {
        const history = useHistory();

        const product = useCreatePurchaseStore((state) =>
            itemExperience ? null : state.getActiveProduct<Experience>(),
        );
        const experience = itemExperience ?? product;

        const fetchParams = {
            "unitType[in]": [LineItemType.ADDON, LineItemType.PICKUP].join(","),
            object: CatalogItemObject.PRODUCT_CATALOG_ITEM,
        };
        // This is here as I need the catalogItems but can't get it directly from the previous experience as it's unknown on this stage
        const { data: sellerAddons } = useCatalogItems(fetchParams, { enabled: shouldShowPurchaseAddons });

        const { data: deletedAddons } = useCatalogItems(
            { ...fetchParams, "deletedAt[exists]": true },
            { enabled: shouldShowPurchaseAddons },
        );

        const filteredDeletedAddons = deletedAddons?.filter((addon) => !isQuestionnaireAddOn(addon));

        const activeLineItems = useCreatePurchaseStore((state) => (itemLineItems ? [] : state.getAddonLineItems()));
        const lineItems = itemLineItems ?? activeLineItems ?? [];

        // useEffect(() => {}, [activeLineItems, shouldCache]);

        const [animationParent] = useAutoAnimate<HTMLDivElement>();

        const productCatalogItem = catalogItems ? catalogItems : experience ? getAddOns(experience) : [];

        // Filter out choices catalog items which have all options deleted or they are choice options
        const filteredCatalogItems = productCatalogItem.filter(
            (catalogItem) =>
                catalogItem.type !== null &&
                !(catalogItem.type === CatalogType.CHOICES && catalogItem.items?.every((item) => item.deletedAt)) &&
                !isQuestionnaireAddOn(catalogItem),
        );

        if (filteredCatalogItems.length === 0) {
            return null;
        }

        const sortedAddons = getSortedAddOns(filteredCatalogItems as AddOn[]);
        const shouldShowDefaultImgInAddOnsList = history.location.pathname === "/cart";

        const sortedAddonsTemplateIds = new Set(
            sortedAddons.flatMap((catalog) => [
                catalog.template?.id,
                ...(catalog.items?.map((item) => item.template?.id) ?? []),
            ]),
        );

        const addonsFromPurchaseItemsLineItem =
            (lineItems as Array<LineItem | CatalogLineItem>).filter(
                (lineItem) =>
                    !!lineItem.quantity &&
                    lineItem.includeInAmount &&
                    !sortedAddonsTemplateIds.has(lineItem.template.id),
            ) ?? [];

        // Find deleted catalog items that match line item templates, prioritizing items with matching product IDs that are not already in sortedAddonsTemplateIds
        const foundTemplateItems = new Map<string, CatalogItem>();
        for (const lineItem of addonsFromPurchaseItemsLineItem) {
            for (const deletedCatalogItem of filteredDeletedAddons) {
                if (isCatalogItemMatchingLineItemTemplate(deletedCatalogItem, lineItem)) {
                    const existingItem = foundTemplateItems.get(lineItem.template.id);
                    if (existingItem) {
                        if (deletedCatalogItem.product?.id === experience?.id) {
                            foundTemplateItems.set(lineItem.template?.id, deletedCatalogItem);
                        }
                    } else {
                        foundTemplateItems.set(lineItem.template.id, deletedCatalogItem);
                    }
                }
            }
        }

        const deletedAddonsInPurchaseItem = [...foundTemplateItems.values()] as AddOn[];

        // Filter line items that do not have matching templates in deleted catalog items
        const nonMatchingLineItems = addonsFromPurchaseItemsLineItem.filter(
            (lineItem) =>
                !deletedAddonsInPurchaseItem.some((catalogItem) =>
                    isCatalogItemMatchingLineItemTemplate(catalogItem, lineItem),
                ),
        ) as LineItem[];

        // Searching seller addons that correspond to lineItems, addOn.items?.some((item) is for choices, as they don't have template.id on the parent level

        const previousExperienceCatalogItems = (nonMatchingLineItems
            .map((lineItem: CatalogLineItem | LineItem) => {
                for (const sellerAddon of sellerAddons) {
                    if (isQuestionnaireAddOn(sellerAddon)) {
                        continue; // Skip questionnaire add-ons
                    }
                    const isMatchingTemplate = sellerAddon.template?.id === lineItem.template.id;
                    const isMatchingChoice = sellerAddon.items?.some(
                        (item) => item.template?.id === lineItem.template.id,
                    );
                    const hasQuantity = lineItem.quantity;
                    // If previous item had a choice addon selected, item lineItem will have template choice addon's template id. We need the dropdown addon for this choice

                    if (isMatchingTemplate || (isMatchingChoice && hasQuantity)) {
                        if (!sellerAddon.type) {
                            const dropdownAddon = sellerAddons.find((addon) =>
                                addon.items?.find((item) => item.template?.id === sellerAddon.template?.id),
                            );

                            if (!dropdownAddon || isQuestionnaireAddOn(dropdownAddon)) {
                                continue;
                            }

                            return dropdownAddon;
                        }

                        return sellerAddon;
                    }
                }

                return null;
            })
            // This is necessary because the find method can return undefined if no matching element is found,
            // which would make TypeScript infer the type as (Addon | undefined)[].
            // Typecast is safe due to the filter(Boolean) call
            .filter(Boolean) ?? []) as AddOn[];

        if (
            productCatalogItem.length === 0 &&
            previousExperienceCatalogItems.length === 0 &&
            deletedAddonsInPurchaseItem.length === 0
        ) {
            return null;
        }

        return (
            <>
                <div className="flex items-center space-x-3 py-2 pt-6">
                    <span className="text-lg font-bold tracking-tight">Add-ons</span>
                    {sortedAddons.length > 0 && (
                        <Badge color="warning" size="small" icon={<BoxIcon className="top-0" />}>
                            {sortedAddons.length}
                        </Badge>
                    )}
                </div>

                <div ref={animationParent} data-testid="addon-list">
                    <ExpandableList
                        entities={sortedAddons}
                        component={(catalog) => (
                            <AddOnItem
                                key={catalog.id + "current"}
                                shouldShowDefaultImgInAddOnsList={shouldShowDefaultImgInAddOnsList}
                                catalog={catalog}
                                purchaseId={purchaseId}
                                lineItems={lineItems}
                                onLineItemUpdate={onLineItemUpdate}
                                onChoiceLineItemUpdate={onChoiceLineItemUpdate}
                            />
                        )}
                        styles={{ button: { className: "mt-4", size: "medium", counter: false } }}
                    >
                        {previousExperienceCatalogItems?.length > 0 ? (
                            <>
                                {sortedAddons.length > 0 && <hr className="mt-2 mb-2 text-gray-light" />}
                                {previousExperienceCatalogItems.map((catalog: AddOn) => (
                                    <AddOnItem
                                        key={catalog.id + "previous"}
                                        shouldDecreaseOnly
                                        shouldShowDefaultImgInAddOnsList={shouldShowDefaultImgInAddOnsList}
                                        catalog={catalog}
                                        purchaseId={purchaseId}
                                        lineItems={lineItems}
                                        onLineItemUpdate={onLineItemUpdate}
                                        onChoiceLineItemUpdate={onChoiceLineItemUpdate}
                                    />
                                ))}
                            </>
                        ) : null}
                        {deletedAddonsInPurchaseItem.length > 0 ? (
                            <div>
                                {deletedAddonsInPurchaseItem.map((catalog) => (
                                    <AddOnItem
                                        key={catalog.id + "deleted"}
                                        shouldDecreaseOnly
                                        shouldShowDefaultImgInAddOnsList={shouldShowDefaultImgInAddOnsList}
                                        catalog={catalog}
                                        purchaseId={purchaseId}
                                        lineItems={lineItems}
                                        onLineItemUpdate={onLineItemUpdate}
                                        onChoiceLineItemUpdate={onChoiceLineItemUpdate}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </ExpandableList>
                </div>
            </>
        );
    },
);

CreatePurchaseAddOnList.displayName = "CreatePurchaseAddOnList";

const getTemplateItemByCatalogType = (catalog: CatalogItem, type: CatalogType) => {
    if (catalog.type === type && catalog.template?.type === LineItemType.ADDON) {
        return catalog.template;
    }

    return null;
};

interface AddOnItemProps {
    shouldShowDefaultImgInAddOnsList: boolean;
    shouldDecreaseOnly?: boolean;
    catalog: AddOn;
    lineItems: CatalogLineItem[] | LineItem[] | AddonLineItem[];
    purchaseId?: ID;
    onLineItemUpdate: (value: number, id?: ID) => void;
    onChoiceLineItemUpdate: (value: ID[], id: ID, shouldDecreaseOnly: boolean) => void;
}

export const AddOnItem = ({
    shouldShowDefaultImgInAddOnsList,
    shouldDecreaseOnly = false,
    catalog,
    lineItems,
    purchaseId,
    onLineItemUpdate,
    onChoiceLineItemUpdate,
}: AddOnItemProps) => {
    const addOnLimits = useAddOnLimits();
    const { isPastDated, shouldAllowSavedQuantity, guestCount, errors } = addOnLimits;
    const { catalogItemOpen, overrideRestrictions, onOverrideRestrictionsChange } = addOnLimits;
    const quantityTemplateItem = getTemplateItemByCatalogType(catalog, CatalogType.QUANTITY);
    const booleanTemplateItem = getTemplateItemByCatalogType(catalog, CatalogType.BOOLEAN);
    const templateId = (quantityTemplateItem ?? booleanTemplateItem)?.id;
    const isOverriding = Boolean(templateId && overrideRestrictions?.[templateId]);
    const limitError = errors?.[catalog.id] ?? (templateId ? errors?.[templateId] : undefined);
    const addOnCatalogItemOpen = templateId ? catalogItemOpen?.[templateId] : undefined;

    const handleChoiceChange = (templateIds: ID[], id: ID) => {
        onChoiceLineItemUpdate(templateIds, id, shouldDecreaseOnly);
    };

    const handleOverrideChange = (value: boolean) => {
        onOverrideRestrictionsChange?.(value, templateId);
    };
    const onOverrideChange = onOverrideRestrictionsChange ? handleOverrideChange : undefined;

    if (quantityTemplateItem) {
        return (
            <QuantityAddon
                key={quantityTemplateItem.id}
                shouldShowDefaultImg={shouldShowDefaultImgInAddOnsList}
                shouldDecreaseOnly={shouldDecreaseOnly}
                isPastDated={isPastDated}
                isOverriding={isOverriding}
                shouldAllowSavedQuantity={shouldAllowSavedQuantity}
                catalog={catalog}
                templateItem={quantityTemplateItem}
                lineItems={lineItems}
                purchaseId={purchaseId}
                guestCount={guestCount}
                limitError={limitError}
                catalogItemOpen={addOnCatalogItemOpen}
                onChange={onLineItemUpdate}
                onOverrideChange={onOverrideChange}
            />
        );
    }

    if (catalog.type === CatalogType.CHOICES) {
        return (
            <ChoicesAddon
                key={catalog.id}
                shouldShowDefaultImg={shouldShowDefaultImgInAddOnsList}
                catalogItem={catalog}
                lineItems={lineItems}
                shouldDecreaseOnly={shouldDecreaseOnly}
                onChange={handleChoiceChange}
            />
        );
    }

    if (booleanTemplateItem) {
        return (
            <BooleanAddon
                key={booleanTemplateItem.id}
                shouldShowDefaultImg={shouldShowDefaultImgInAddOnsList}
                shouldDecreaseOnly={shouldDecreaseOnly}
                isOverriding={isOverriding}
                catalogItem={catalog}
                templateItem={booleanTemplateItem}
                lineItems={lineItems}
                limitError={limitError}
                catalogItemOpen={addOnCatalogItemOpen}
                onChange={onLineItemUpdate}
                onOverrideChange={onOverrideChange}
            />
        );
    }

    return null;
};

const isCatalogItemMatchingLineItemTemplate = (catalogItem: CatalogItem, lineItem: LineItem | CatalogLineItem) => {
    return (
        catalogItem.template?.id === lineItem.template.id ||
        (catalogItem.items?.some((item) => item.template?.id === lineItem.template.id) &&
            !isQuestionnaireAddOn(catalogItem)) // Ensure we don't match questionnaire add-ons
    );
};
